import { store } from '../store';
import { 
  fetchActiveEvent, 
  setupGigsListener, 
  setupEventDocListener, 
  cleanupAllListeners,
  updateEventMainData,
  updateGigs,
  fetchDayliteOpportunity,
  updateDayliteData
} from '../store/slices/activeEventSlice';
import { LoaderFunctionArgs, redirect } from 'react-router';

/**
 * Fetches extended event details from Daylite in a non-blocking way
 * This function doesn't return anything and doesn't throw errors
 * as it's meant to be fire-and-forget
 * 
 * @param opportunityId The ID of the Daylite opportunity to fetch
 */
function fetchExtendedEventDetails(opportunityId: string): void {
  // Dispatch the async thunk but don't await it
  // This will automatically update the store when complete
  store.dispatch(fetchDayliteOpportunity(opportunityId)).catch(error => {
    console.error('Error fetching Daylite opportunity data:', error);
    // Errors are already logged in the thunk and won't break the app
  });
}

// Loader for the single event page
export async function singleEventLoader({ request }: LoaderFunctionArgs) {
  // Get the event ID from URL search parameters
  const url = new URL(request.url);
  let eventId = url.searchParams.get('id');

  // Treat empty / whitespace-only IDs as missing.
  if (eventId !== null && eventId.trim() === '') {
    eventId = null;
  }

  // The route URL is the source of truth. Do not silently fall back to a
  // persisted Redux ID here: that can load the wrong event on direct links,
  // reloads, or history navigation.
  if (!eventId) {
    // The layout catches this and surfaces it via the snackbar +
    // inline error UI. Do NOT silently throw without a usable message.
    throw new Error(
      'Event ID is required. Please navigate to this event from the All Events list or use a full event link.'
    );
  }

  const resolvedEventId = eventId;

  // Create a promise that will be resolved when data is ready
  const eventDataPromise = (async () => {
    try {
      // First clean up any existing listeners to prevent stale data
      await store.dispatch(cleanupAllListeners()).unwrap();

      // Clear specific data that might be stale without resetting the whole state.
      // Use `undefined` for mainData to signal "not loaded yet" (vs. `null`
      // which the snapshot handler uses for "checked, doc does not exist").
      store.dispatch(updateGigs([]));
      store.dispatch(updateEventMainData(undefined));
      store.dispatch(updateDayliteData(null)); // Reset Daylite data to null

      // Then fetch the event data
      await store.dispatch(fetchActiveEvent(resolvedEventId)).unwrap();

      // Set up the gigs listener. With the updated thunk semantics, this
      // promise does NOT resolve until the FIRST Firestore snapshot has
      // arrived (success or error). That guarantees the talent list never
      // renders before real data is available — eliminating the race that
      // caused users to see an empty grid for events that actually had gigs.
      await store.dispatch(setupGigsListener(resolvedEventId)).unwrap();

      // Also setup the event document listener
      await store.dispatch(setupEventDocListener(resolvedEventId)).unwrap();

      // Fetch extended Daylite data in a non-blocking way
      fetchExtendedEventDetails(resolvedEventId);

      // Return the event data from the store
      return store.getState().activeEvent;
    } catch (error) {
      console.error('Failed to load event:', error);
      throw error;
    }
  })();

  // Return the promise
  return {
    eventData: eventDataPromise
  };
}
